// PEO Organizational Hierarchy Types

export type PEORoleType = 
  | 'sales_rep' 
  | 'senior_sales_rep'
  | 'sales_manager' 
  | 'senior_sales_manager'
  | 'underwriter' 
  | 'senior_underwriter'
  | 'department_head'
  | 'regional_manager'
  | 'operations_manager'
  | 'admin';

export type PermissionLevel = 
  | 'own_only'      // Can only see their own data
  | 'team_only'     // Can see their direct reports' data
  | 'department'    // Can see entire department data
  | 'regional'      // Can see regional data
  | 'all';         // Can see all company data

export interface PEOOrganizationalRole {
  id: string;
  provider_id: string;
  role_name: string;
  role_type: PEORoleType;
  permission_level: PermissionLevel;
  description?: string;
  
  // Permissions for different data types
  can_view_applications: boolean;
  can_create_applications: boolean;
  can_edit_applications: boolean;
  can_delete_applications: boolean;
  
  can_view_quotes: boolean;
  can_create_quotes: boolean;
  can_edit_quotes: boolean;
  can_delete_quotes: boolean;
  
  can_view_analytics: boolean;
  can_manage_team: boolean;
  can_manage_roles: boolean;
  
  created_at: string;
  updated_at: string;
}

export interface PEOTeamStructure {
  id: string;
  provider_id: string;
  parent_user_id?: string;  // Manager/supervisor
  child_user_id: string;    // Direct report
  team_name?: string;
  department?: string;
  region?: string;
  hierarchy_level: number;  // 1 = top level, higher numbers = deeper
  
  created_at: string;
  updated_at: string;
  created_by: string;
}

export interface EnhancedPEORepresentative {
  id: string;
  user_id: string;
  provider_id: string;
  verification_status: 'pending' | 'verified' | 'rejected';
  
  // Enhanced organizational fields
  organizational_role_id?: string;
  team_name?: string;
  department?: string;
  region?: string;
  hire_date?: string;
  manager_user_id?: string;
  is_manager: boolean;
  territory?: string[];
  
  created_at: string;
  updated_at: string;
  
  // Joined data
  organizational_role?: PEOOrganizationalRole;
  profile?: {
    id: string;
    first_name?: string;
    last_name?: string;
    email?: string;
  };
  manager_profile?: {
    id: string;
    first_name?: string;
    last_name?: string;
    email?: string;
  };
}

export interface PEOTeamMember extends EnhancedPEORepresentative {
  direct_reports?: PEOTeamMember[];
  hierarchy_level?: number;
}

export interface PEOOrgChart {
  provider_id: string;
  provider_name: string;
  members: PEOTeamMember[];
  departments: string[];
  teams: string[];
  regions: string[];
}

// Role template for creating new roles
export interface RoleTemplate {
  role_type: PEORoleType;
  default_permissions: Omit<PEOOrganizationalRole, 'id' | 'provider_id' | 'role_name' | 'created_at' | 'updated_at'>;
}

// Permission check result
export interface PermissionCheckResult {
  can_access: boolean;
  reason?: string;
  scope: 'own' | 'team' | 'department' | 'regional' | 'all';
}

// User access scope for filtering data
export interface UserAccessScope {
  user_id: string;
  provider_id: string;
  permission_level: PermissionLevel;
  accessible_user_ids: string[];
  departments: string[];
  regions: string[];
  teams: string[];
}

// Org chart node for visualization
export interface OrgChartNode {
  id: string;
  user_id: string;
  name: string;
  title: string;
  role_type: PEORoleType;
  department?: string;
  team?: string;
  region?: string;
  parent_id?: string;
  children: OrgChartNode[];
  level: number;
  is_manager: boolean;
  direct_reports_count: number;
}

// Team management operations
export interface TeamManagementOperation {
  type: 'assign_role' | 'change_manager' | 'move_team' | 'update_permissions';
  user_id: string;
  target_id?: string;
  new_value: any;
  reason?: string;
}

// Permission matrix for role comparison
export interface PermissionMatrix {
  role_id: string;
  role_name: string;
  permissions: {
    [key: string]: boolean;
  };
}

export const ROLE_TEMPLATES: Record<PEORoleType, RoleTemplate> = {
  sales_rep: {
    role_type: 'sales_rep',
    default_permissions: {
      role_type: 'sales_rep',
      permission_level: 'own_only',
      description: 'Standard sales representative with access to own accounts only',
      can_view_applications: true,
      can_create_applications: true,
      can_edit_applications: true,
      can_delete_applications: false,
      can_view_quotes: true,
      can_create_quotes: true,
      can_edit_quotes: true,
      can_delete_quotes: false,
      can_view_analytics: false,
      can_manage_team: false,
      can_manage_roles: false,
    }
  },
  senior_sales_rep: {
    role_type: 'senior_sales_rep',
    default_permissions: {
      role_type: 'senior_sales_rep',
      permission_level: 'own_only',
      description: 'Senior sales representative with enhanced quote permissions',
      can_view_applications: true,
      can_create_applications: true,
      can_edit_applications: true,
      can_delete_applications: false,
      can_view_quotes: true,
      can_create_quotes: true,
      can_edit_quotes: true,
      can_delete_quotes: true,
      can_view_analytics: true,
      can_manage_team: false,
      can_manage_roles: false,
    }
  },
  sales_manager: {
    role_type: 'sales_manager',
    default_permissions: {
      role_type: 'sales_manager',
      permission_level: 'team_only',
      description: 'Sales manager with access to team data and analytics',
      can_view_applications: true,
      can_create_applications: true,
      can_edit_applications: true,
      can_delete_applications: true,
      can_view_quotes: true,
      can_create_quotes: true,
      can_edit_quotes: true,
      can_delete_quotes: true,
      can_view_analytics: true,
      can_manage_team: true,
      can_manage_roles: false,
    }
  },
  senior_sales_manager: {
    role_type: 'senior_sales_manager',
    default_permissions: {
      role_type: 'senior_sales_manager',
      permission_level: 'department',
      description: 'Senior sales manager with departmental access',
      can_view_applications: true,
      can_create_applications: true,
      can_edit_applications: true,
      can_delete_applications: true,
      can_view_quotes: true,
      can_create_quotes: true,
      can_edit_quotes: true,
      can_delete_quotes: true,
      can_view_analytics: true,
      can_manage_team: true,
      can_manage_roles: true,
    }
  },
  underwriter: {
    role_type: 'underwriter',
    default_permissions: {
      role_type: 'underwriter',
      permission_level: 'all',
      description: 'Underwriter with full access to all applications and quotes',
      can_view_applications: true,
      can_create_applications: false,
      can_edit_applications: true,
      can_delete_applications: false,
      can_view_quotes: true,
      can_create_quotes: true,
      can_edit_quotes: true,
      can_delete_quotes: false,
      can_view_analytics: true,
      can_manage_team: false,
      can_manage_roles: false,
    }
  },
  senior_underwriter: {
    role_type: 'senior_underwriter',
    default_permissions: {
      role_type: 'senior_underwriter',
      permission_level: 'all',
      description: 'Senior underwriter with full access and deletion rights',
      can_view_applications: true,
      can_create_applications: true,
      can_edit_applications: true,
      can_delete_applications: true,
      can_view_quotes: true,
      can_create_quotes: true,
      can_edit_quotes: true,
      can_delete_quotes: true,
      can_view_analytics: true,
      can_manage_team: false,
      can_manage_roles: false,
    }
  },
  department_head: {
    role_type: 'department_head',
    default_permissions: {
      role_type: 'department_head',
      permission_level: 'department',
      description: 'Department head with full departmental access',
      can_view_applications: true,
      can_create_applications: true,
      can_edit_applications: true,
      can_delete_applications: true,
      can_view_quotes: true,
      can_create_quotes: true,
      can_edit_quotes: true,
      can_delete_quotes: true,
      can_view_analytics: true,
      can_manage_team: true,
      can_manage_roles: true,
    }
  },
  regional_manager: {
    role_type: 'regional_manager',
    default_permissions: {
      role_type: 'regional_manager',
      permission_level: 'regional',
      description: 'Regional manager with access to regional data',
      can_view_applications: true,
      can_create_applications: true,
      can_edit_applications: true,
      can_delete_applications: true,
      can_view_quotes: true,
      can_create_quotes: true,
      can_edit_quotes: true,
      can_delete_quotes: true,
      can_view_analytics: true,
      can_manage_team: true,
      can_manage_roles: true,
    }
  },
  operations_manager: {
    role_type: 'operations_manager',
    default_permissions: {
      role_type: 'operations_manager',
      permission_level: 'all',
      description: 'Operations manager with full operational access',
      can_view_applications: true,
      can_create_applications: true,
      can_edit_applications: true,
      can_delete_applications: true,
      can_view_quotes: true,
      can_create_quotes: true,
      can_edit_quotes: true,
      can_delete_quotes: true,
      can_view_analytics: true,
      can_manage_team: true,
      can_manage_roles: true,
    }
  },
  admin: {
    role_type: 'admin',
    default_permissions: {
      role_type: 'admin',
      permission_level: 'all',
      description: 'Full administrative access to all data and team management',
      can_view_applications: true,
      can_create_applications: true,
      can_edit_applications: true,
      can_delete_applications: true,
      can_view_quotes: true,
      can_create_quotes: true,
      can_edit_quotes: true,
      can_delete_quotes: true,
      can_view_analytics: true,
      can_manage_team: true,
      can_manage_roles: true,
    }
  },
};

export const PERMISSION_LABELS: Record<PermissionLevel, string> = {
  own_only: 'Own Data Only',
  team_only: 'Team Data',
  department: 'Department Data',
  regional: 'Regional Data',
  all: 'All Company Data'
};

export const ROLE_TYPE_LABELS: Record<PEORoleType, string> = {
  sales_rep: 'Sales Representative',
  senior_sales_rep: 'Senior Sales Representative',
  sales_manager: 'Sales Manager',
  senior_sales_manager: 'Senior Sales Manager',
  underwriter: 'Underwriter',
  senior_underwriter: 'Senior Underwriter',
  department_head: 'Department Head',
  regional_manager: 'Regional Manager',
  operations_manager: 'Operations Manager',
  admin: 'Administrator'
};